import React, { useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Button, LoadingSpinner } from '../../../components';
import { AgentBadge } from '../../../components/AgentBadge';
import { AgentCapabilityChips } from '../../../components/AgentCapabilityChips';
import { AgentChainChips } from '../../../components/AgentChainChips';
import { KlyxOperatedBadge } from '../../../components/KlyxOperatedBadge';
import { ProfileSpotlightButton } from '../../../components/ProfileSpotlightButton';
import { useAppDispatch, useAppSelector } from '../../../redux/Store';
import { fetchOwnerAgents } from '../../../redux/thunks/agentThunks';
import {
  selectOwnedAgents,
  selectOwnedAgentsStatus,
  selectOwnedAgentsError,
} from '../../../redux/selectors/agentSelectors';
import './MyAgents.scss';

/**
 * "My Agents" — ADR-011 Ring 1.
 *
 * Owner sees the agents they've registered, sorted newest-first,
 * with enough detail per row (avatar, display name, on-chain
 * profile id, SFT nonce, custody mode) to identify each. Each row
 * links to the agent's public profile page.
 *
 * Empty state guides first-time users to /agents/register.
 */
const MyAgents: React.FC = () => {
  const dispatch = useAppDispatch();
  const agents = useAppSelector(selectOwnedAgents);
  const status = useAppSelector(selectOwnedAgentsStatus);
  const error = useAppSelector(selectOwnedAgentsError);

  useEffect(() => {
    dispatch(fetchOwnerAgents());
  }, [dispatch]);

  return (
    <div className="my-agents">
      <div className="my-agents-container">
        <header className="my-agents-header">
          <div className="header-left">
            <h1>My Agents</h1>
            <p>
              Agents you've registered on Klyx — each has its own
              on-chain SFT and wallet, owned by you.
            </p>
          </div>
          {/* Two-button action row. Register (primary) is the main
              call-to-action for owners who came here to add another
              agent; Discover (secondary) is the shortcut to the
              public directory, kept adjacent since owners often
              want to see the wider ecosystem their agent will land
              into. Both entry points also live in the main nav /
              user menu — this is the on-page shortcut. */}
          <div className="my-agents-header__actions">
            <Link to="/agents/discover" className="register-cta">
              <Button variant="secondary">Discover agents</Button>
            </Link>
            <Link to="/agents/register" className="register-cta">
              <Button>Register agent</Button>
            </Link>
          </div>
        </header>

        {status === 'loading' && (
          <div className="my-agents-loading">
            <LoadingSpinner size="medium" message="Loading your agents…" />
          </div>
        )}

        {status === 'rejected' && (
          <div className="my-agents-error" role="alert">
            {error ?? 'Failed to load your agents.'}
          </div>
        )}

        {status === 'loaded' && agents.length === 0 && (
          <div className="my-agents-empty">
            <div className="empty-icon" aria-hidden="true">🤖</div>
            <h2>No agents yet</h2>
            <p>
              Register your first agent to give it a Klyx identity,
              on-chain SFT, and a public profile.
            </p>
            <Link to="/agents/register">
              <Button>Register your first agent</Button>
            </Link>
          </div>
        )}

        {status === 'loaded' && agents.length > 0 && (
          <ul className="my-agents-list">
            {agents.map((agent) => {
              const displayName = agent.displayName ?? agent.username;
              const profileHref = `/profile/${agent.username}`;
              const editHref = `/agents/mine/${agent.userId}/edit`;
              // Restructured so the meta strip lives OUTSIDE the profile
              // Link. The Edit link is a sibling of the profile Link, not
              // nested inside it (nesting <a> tags is invalid HTML and
              // React Router event bubbling to the outer Link makes
              // clicking Edit navigate to the profile page instead).
              return (
                <li key={agent.userId} className="my-agents-item">
                  <Link to={profileHref} className="my-agents-item-link">
                    <div className="agent-avatar">
                      {agent.avatarUrl ? (
                        <img src={agent.avatarUrl} alt="" />
                      ) : (
                        <span aria-hidden="true">🤖</span>
                      )}
                    </div>
                    <div className="agent-main">
                      <div className="agent-name-row">
                        <span className="agent-display-name">{displayName}</span>
                        <AgentBadge isAgent />
                        <KlyxOperatedBadge isKlyxOperated={agent.isKlyxOperated} />
                      </div>
                      <div className="agent-handle">@{agent.username}</div>
                      {agent.bio && (
                        <p className="agent-bio">{agent.bio}</p>
                      )}
                      <AgentCapabilityChips
                        capabilities={agent.capabilities}
                        size="sm"
                        className="agent-caps"
                      />
                      <AgentChainChips
                        chains={agent.chains}
                        size="sm"
                        className="agent-caps"
                      />
                    </div>
                  </Link>
                  <div className="agent-meta-bar">
                    {/* Profile ID + SFT nonce dropped from the meta
                        bar — they're on-chain plumbing not useful to
                        the owner-scoped My Agents view. Still visible
                        via the /profile/:username surface + Explorer
                        for anyone who needs them. */}
                    <dl className="agent-meta">
                      <div>
                        <dt>Custody</dt>
                        <dd>
                          {agent.custodyMode === 'managed'
                            ? 'Managed by Klyx'
                            : 'Self-custody'}
                        </dd>
                      </div>
                      <div>
                        <dt>Endpoints</dt>
                        <dd>{agent.endpoints.length}</dd>
                      </div>
                      <div>
                        <dt>Lanes</dt>
                        <dd>{agent.laneCount}</dd>
                      </div>
                      <div>
                        <dt>Receipts</dt>
                        <dd>{agent.receiptCount}</dd>
                      </div>
                    </dl>
                    <div className="agent-actions">
                      <Link to={editHref} className="agent-edit-link">
                        Edit
                      </Link>
                      {/* Global-scope spotlight button — requires
                          BOTH the DB nft_profile row (nftProfileUuid)
                          AND a confirmed on-chain mint (profileId
                          non-null). The button used to only check
                          nftProfileUuid, which let managed-custody
                          agents whose on-chain confirmation hadn't
                          landed yet render a button that 400'd on
                          the backend with `Cannot convert null to a
                          BigInt` (#675). */}
                      {agent.nftProfileUuid && agent.profileId != null && (
                        <ProfileSpotlightButton
                          profileId={agent.nftProfileUuid}
                          profileUsername={agent.username}
                          profileDisplayName={agent.displayName ?? undefined}
                          audienceScope="global"
                          className="agent-spotlight-btn"
                        />
                      )}
                    </div>
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </div>
    </div>
  );
};

export default MyAgents;
